import { useEffect, useState } from "react";

const FALLBACK_BG = "https://images.unsplash.com/photo-1603484477859-abe6a73f9366?crop=entropy&cs=srgb&fm=jpg&w=600&q=85";
const CACHE_KEY = "read_cover_cache_v1";

function readCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}"); } catch { return {}; }
}
function writeCache(map) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(map)); } catch {}
}

async function lookupCover(title, author) {
  if (!title) return "";
  const params = new URLSearchParams({
    title,
    limit: "1",
    fields: "cover_i",
  });
  if (author) params.set("author", author);
  try {
    const res = await fetch(`https://openlibrary.org/search.json?${params}`);
    const data = await res.json();
    const doc = data?.docs?.[0];
    if (doc?.cover_i) return `https://covers.openlibrary.org/b/id/${doc.cover_i}-L.jpg`;
  } catch {}
  return "";
}

async function imageLoadable(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

export function BookCover({ src, title, author, className = "", testId }) {
  const [imgSrc, setImgSrc] = useState(src || "");
  const [errored, setErrored] = useState(false);
  const [tried, setTried] = useState(false);

  useEffect(() => {
    setImgSrc(src || "");
    setErrored(false);
    setTried(false);
  }, [src]);

  // Auto-fetch from Open Library if no manual cover URL
  useEffect(() => {
    if (src || tried || !title || title.length < 3) return;
    const placeholder = title.toLowerCase().trim();
    if (placeholder === "untitled" || placeholder === "unknown") return;
    setTried(true);
    const key = `${title.toLowerCase()}::${(author || "").toLowerCase()}`;
    const cache = readCache();
    if (key in cache) {
      if (cache[key]) setImgSrc(cache[key]);
      return;
    }
    lookupCover(title, author).then((url) => {
      cache[key] = url;
      writeCache(cache);
      if (url) setImgSrc(url);
    });
  }, [src, title, author, tried]);

  const showImage = imgSrc && !errored;

  return (
    <div
      className={`relative aspect-[2/3] overflow-hidden border border-[rgba(0,0,0,0.10)] shadow-book book-cover bg-[#E8DFC1] ${className}`}
      data-testid={testId}
    >
      {showImage ? (
        <img
          src={imgSrc}
          alt={title}
          loading="lazy"
          onError={() => setErrored(true)}
          className="w-full h-full object-cover"
        />
      ) : (
        <div
          className="w-full h-full relative flex flex-col items-center justify-center text-center p-4"
          style={{ backgroundImage: `linear-gradient(rgba(250,250,250,0.92), rgba(250,250,250,0.92)), url(${FALLBACK_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        >
          <div className="font-heading text-[#0A0A0A] text-lg leading-tight line-clamp-4">{title}</div>
          {author && <div className="mt-2 font-mono uppercase tracking-widest text-[10px] text-[#525252]">{author}</div>}
        </div>
      )}
      <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-r from-black/10 to-transparent pointer-events-none" />
    </div>
  );
}
